import styled from 'styled-components'
import { Column } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import { type TestimonialsData } from '@/features/cms/components/CMS/types'
import { TestimonialCard } from '@/features/cms/components/CMS/components/Testimonials/TestimonialCard'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { BREAKPOINTS } from '@shipt/design-system-themes'
import { CarouselSection } from '@shipt/design-system-carousel'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'

/**
 * This component corresponds to the `testimonial` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/testimonial
 * - **Production** - https://cms.shipt.com/content-type/pool/testimonial
 */
export const TestimonialSection = ({
  content_type_id,
  id,
  data: { heading, subheading, testimonial_cards },
}: TestimonialsData) => {
  const { trackCMSElementClicked, trackCMSElementViewed } = useCMSTrackingEvents()

  return (
    <Wrapper {...getCMSWrapperProps({ content_type_id, id })} as="section">
      <CarouselSection hasInlineControls className="fullWidth contentAtTablet">
        <ScrollIntoView
          onInView={() => {
            trackCMSElementViewed({
              content: 'Testimonial',
            })
          }}
        >
          <Column align="center" spacing={{ base: 'md', md: '3xl' }} padding={[0, 'lg']}>
            <Headline as="h2" size="xxl" weight="medium" align="center">
              {heading}
            </Headline>
            <Body size="xl" align="center">
              {subheading}
            </Body>
          </Column>
          <CarouselSection.Carousel
            canAutoplay
            hasPagination
            hasInlineControls
            centeredSlides
            loop
            onSlideChange={() => {
              trackCMSElementClicked({
                type: 'button',
                content: 'testimonial navigation',
              })
            }}
            layoutConfig={{
              // @ts-expect-error -- https://app.clickup.com/t/86b6q2de4
              0: {
                slidesOffsetAfter: 0,
                slidesOffsetBefore: 0,
                slidesPerView: 1.5,
                slidesPerGroup: 1,
              },
              [BREAKPOINTS.SMALL]: {
                slidesOffsetAfter: 0,
                slidesOffsetBefore: 0,
                slidesPerView: 1.5,
                slidesPerGroup: 1,
              },
              [BREAKPOINTS.MEDIUM]: {
                slidesOffsetAfter: 0,
                slidesOffsetBefore: 0,
                slidesPerView: 2.25,
                slidesPerGroup: 1,
              },
              1024: {
                slidesOffsetAfter: 0,
                slidesOffsetBefore: 0,
                slidesPerView: 2.25,
                slidesPerGroup: 1,
              },
              [BREAKPOINTS.LARGE]: {
                slidesOffsetAfter: 0,
                slidesOffsetBefore: 0,
                slidesPerView: 2.25,
                slidesPerGroup: 1,
              },
            }}
          >
            {testimonial_cards.map((testimonial) => (
              <TestimonialCard key={testimonial.data.name} card={testimonial} />
            ))}
          </CarouselSection.Carousel>
        </ScrollIntoView>
      </CarouselSection>
    </Wrapper>
  )
}

const Wrapper = styled(GridLayoutContainer)`
  --content-max-width: 1280px;
`
